/**
 * Booking Details Card
 * 
 * Flexible card component for displaying booking or subscription details.
 * Used in payment and confirmation flows.
 * 
 * @module butterUI/components/BookingDetailsCard
 */

import { BayImage } from '#components/data-display';

export interface BookingDetailsCardProps {
  /** Card title (defaults to "Booking Details") */
  title?: string;
  
  /** Image URL (optional - no image shown if not provided) */
  imageUrl?: string | null;
  /** Image alt text */
  imageAlt?: string;
  /** Bay ID for placeholder generation when imageUrl is not provided */
  bayId?: number;
  
  /** Item name (bay name, subscription name, etc.) */
  name?: string;
  
  // Booking-specific props (optional)
  /** 
   * Formatted date string (e.g., "Friday, 21 November, 2025")
   * Use `formatClubDate(date, timezone, 'EEEE, d MMMM, yyyy')` from @butter/library/datetime
   */
  date?: string;
  /** 
   * Time range in 12-hour AM/PM format (e.g., "10:00 AM - 11:00 AM")
   * Format using `formatTimeRange(startTime, endTime)` utility
   */
  time?: string;
  /** Duration string (e.g., "1 Hour") */
  duration?: string;
  
  // Subscription-specific props (optional)
  /** Subscription start date (e.g., "January 28, 2026") */
  startDate?: string;
  /** Billing cycle (e.g., "Monthly", "Annually") */
  billingCycle?: string;
  /** Next billing date (e.g., "February 28, 2026") */
  nextBillingDate?: string;
  /** Subscription type for proper label display */
  subscriptionType?: 'membership' | 'pass';
  
  /** Optional CSS class name */
  className?: string;
}

/**
 * Booking Details Card Component
 * 
 * Displays a read-only summary of booking or subscription details.
 * Flexible component that shows different information based on props provided.
 * 
 * @example Booking usage
 * ```tsx
 * <BookingDetailsCard
 *   imageUrl="https://example.com/bay.jpg"
 *   name="Pebble Beach"
 *   date="Friday, 21 November, 2025"
 *   time="10:00 AM - 11:00 AM"
 *   duration="1 Hour"
 * />
 * ```
 * 
 * @example Subscription usage
 * ```tsx
 * <BookingDetailsCard
 *   title="Billing Details"
 *   startDate="January 28, 2026"
 *   billingCycle="Monthly"
 *   nextBillingDate="February 28, 2026"
 * />
 * ```
 */
export function BookingDetailsCard({
  title = 'Booking Details',
  imageUrl,
  imageAlt,
  bayId = 0,
  name,
  date,
  time,
  duration,
  startDate,
  billingCycle,
  nextBillingDate,
  subscriptionType,
  className = '',
}: BookingDetailsCardProps) {
  // Presence test, NOT truthiness — deliberately distinct from the placeholder
  // fallback below. `undefined` (prop omitted) means "this card has no image slot"
  // and is how the subscription/billing callers render no image at all; `null` means
  // "booking card, bay has no photo" and renders the placeholder. BayImage would
  // happily render a placeholder for both, so this guard has to stay.
  const shouldShowImage = imageUrl !== undefined;
  
  // Determine which details to show
  const showBookingDetails = date || time || duration;
  const showSubscriptionDetails = startDate || billingCycle || nextBillingDate;
  
  // Separate rendering logic for passes vs memberships
  const isPass = subscriptionType === 'pass';
  
  return (
    <div className={className} data-component="BookingDetailsCard"> 
      <h3 className="text-xl font-semibold text-gray-900">
        {title}
      </h3>
      
      <div className="space-y-4 mt-6">
        {/* Image (optional) */}
        {shouldShowImage && (
          <BayImage
            src={imageUrl}
            seed={bayId}
            alt={imageAlt || `${name || 'Item'} image`}
            className="w-full rounded-3.5xl object-cover"
            style={{ aspectRatio: '16 / 10' }}
          />
        )}
        
        {/* Name */}
        {name && (
          <div className='mb-2'>
            <p 
              className="text-base font-medium text-fg-primary" 
              data-testid="booking-details-name">
              {name}
            </p>
          </div>
        )}
        
        {/* Booking Details */}
        {showBookingDetails && (
          <div className="space-y-2 text-sm border-1.25 border-gray-200 rounded-xl p-4">
            {date && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Date</span>
                <span 
                  className="text-fg-primary font-medium" 
                  data-testid="booking-details-date">
                  {date}
                </span>
              </div>
            )}
            
            {time && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Time</span>
                <span 
                  className="text-fg-primary font-medium" 
                  data-testid="booking-details-time">
                  {time}
                </span>
              </div>
            )}
            
            {duration && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Duration</span>
                <span 
                  className="text-fg-primary font-medium" 
                  data-testid="booking-details-duration">
                  {duration}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Pass Subscription Details */}
        {showSubscriptionDetails && isPass && (
          <div className="space-y-2 text-sm border-1.25 border-gray-200 rounded-xl p-4">
            {startDate && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Start Date</span>
                <span className="text-fg-primary font-medium">
                  {startDate}
                </span>
              </div>
            )}
            
            {billingCycle && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Validity Period</span>
                <span className="text-fg-primary font-medium">
                  {billingCycle}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Membership Subscription Details */}
        {showSubscriptionDetails && !isPass && (
          <div className="space-y-2 text-sm border-1.25 border-gray-200 rounded-xl p-4">
            {startDate && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Start Date</span>
                <span className="text-fg-primary font-medium">
                  {startDate}
                </span>
              </div>
            )}
            
            {billingCycle && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Billing Cycle</span>
                <span className="text-fg-primary font-medium">
                  {billingCycle}
                </span>
              </div>
            )}
            
            {nextBillingDate && (
              <div className="flex justify-between">
                <span className="text-fg-tertiary">Next Billing Date</span>
                <span className="text-fg-primary font-medium">
                  {nextBillingDate}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
